import {Inject, Injectable, OnDestroy, Optional} from "@angular/core";
import {BehaviorSubject, concatMap, map, Observable, ReplaySubject, share, Subject, Subscription, toArray} from "rxjs";
import {FUINavigationTree} from "./navigation-trees/navigation-tree/navigation-tree.model";
import {Event, NavigationEnd, Route, Router} from "@angular/router";
import {FUINavigationLink} from "./navigation-link/navigation-link.model";
import {FUINavigationBranch} from "./navigation-branches/navigation-branch/navigation-branch.model";
import {FUIConfig} from "../fui.config";
import {FUIResponsiveService} from "../page/responsive.service";
import {FUIBreakpoints} from "../page/breakpoints.model";
import {HttpClient} from "@angular/common/http";

export abstract class FUINavigationLoader {
    abstract loadNavigation(): Observable<FUINavigationTree[]>;
}

/**
 * Used to maintain state across the various parts of the $navigation
 * tree (tree, branch, group, link). By keeping track of everything
 * here we are able to cascade changes to state based on form-context (when
 * $navigation ends, when a group is open/closed, when a link is selected)
 */
@Injectable()
export class FUINavigationService implements OnDestroy {
    private _activeBranch: BehaviorSubject<FUINavigationBranch> = new BehaviorSubject<FUINavigationBranch>(new FUINavigationBranch);
    public activeBranch$: Observable<FUINavigationBranch> = this._activeBranch.asObservable();
    private _activeGroup: BehaviorSubject<FUINavigationLink> = new BehaviorSubject<FUINavigationLink>(new FUINavigationLink);
    public activeGroup$: Observable<FUINavigationLink> = this._activeGroup.asObservable();
    private _activeLink: BehaviorSubject<FUINavigationLink> = new BehaviorSubject<FUINavigationLink>(new FUINavigationLink);
    public activeLink$: Observable<FUINavigationLink> = this._activeLink.asObservable();
    private _activeModal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public activeModal$: Observable<boolean> = this._activeModal.asObservable();
    private _activeRoute: Subject<Route> = new Subject<Route>();
    public activeRoute$: Observable<Route> = this._activeRoute.asObservable();
    private _activeTree: BehaviorSubject<FUINavigationTree> = new BehaviorSubject<FUINavigationTree>(new FUINavigationTree);
    public activeTree$: Observable<FUINavigationTree> = this._activeTree.asObservable();
    private _routerEvents: Subject<Event> = new Subject<Event>();
    public routerEvents$: Observable<Event> = this._routerEvents.asObservable();
    private trees$: Observable<FUINavigationTree[]>;
    public useMobileNav$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private subscriptions: Subscription = new Subscription();

    constructor(private http: HttpClient,
                private router: Router,
                private responsiveService: FUIResponsiveService,
                @Optional() private navigationLoader: FUINavigationLoader,
                @Optional() @Inject(FUIConfig.NAVIGATION_PATH_KEY) private navigationPath: string) {
        /**
         * Watch for router events to be used
         * for $navigation control. For instance, when
         * a user completes a successful page change:
         * close the $navigation so they are given a cue
         * that something has happened and can work within
         * the page straight away.
         */
        this.subscriptions.add(this.router.events.subscribe(event => event instanceof NavigationEnd && this._routerEvents.next(event)));

        this.useMobileNav$.next(this.useMobileNav());
        this.subscriptions.add(this.responsiveService.window$.subscribe(() => this.useMobileNav$.next(this.useMobileNav())));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public useMobileNav(): boolean {
        return this.responsiveService.breakpointApplies(FUIBreakpoints.smallDown) || (this.responsiveService.isIos && this.responsiveService.breakpointApplies(FUIBreakpoints.shortDown));
    }

    public initialiseNavigationLinks(initialise: (link: FUINavigationLink) => void) {
        this.getTrees().subscribe(trees => trees.forEach(tree => tree.branches.forEach(branch => this.initialiseNavigationLinksArray(initialise, branch.links))));
    }

    /**
     * Get $navigation trees. These are the menu items
     * listed at the top level
     * @returns {Reducer|Observable<R>}
     */
    getTrees(): Observable<FUINavigationTree[]> {
        if (!this.trees$) {
            let trees: Observable<FUINavigationTree[]>;
            if (this.navigationLoader) {
                trees = this.navigationLoader.loadNavigation();
            } else {
                trees = this.http.get(this.navigationPath).pipe(
                    concatMap((response: Response) => response.json()),
                    map((item: any) => new FUINavigationTree(item)),
                    toArray());
            }
            this.trees$ = trees.pipe(share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            }));
        }
        return this.trees$;
    }

    /**
     * Reset modal to disabled state
     */
    resetActiveModal(): void {
        this._activeModal.next(false);
    }

    /**
     * Reset the active tree level (top level)
     */
    resetActiveTree(): void {
        this._activeTree.next(new FUINavigationTree);
    }

    /**
     *  Reset the active group level (tree > branch > group)
     */
    resetActiveGroup(): void {
        this._activeGroup.next(new FUINavigationLink);
    }

    /**
     * Set active states both internally and externally
     * for actions that are relevant to the state of the
     * modal window diplay - such as the overlay in the
     * page component.
     * @param navigationTree
     * @param showModal
     */
    setActiveTree(navigationTree: FUINavigationTree, showModal = true): void {
        if (navigationTree.branches) {
            this._activeTree.next(navigationTree);
            this._activeModal.next(showModal);
        }
    }

    /**
     * Set the active branch
     * @param navigationBranch
     */
    setActiveBranch(navigationBranch: FUINavigationBranch): void {
        this._activeBranch.next(navigationBranch);
    }

    /**
     * Set the active group for reference at different levels
     * and active checking
     * @param navigationGroup
     */
    setActiveGroup(navigationGroup: FUINavigationLink): void {
        if (navigationGroup.links
        ) {
            this._activeGroup.next(navigationGroup);
        }
    }

    /**
     * Set the active link for use throughout the $navigation structure
     * @param navigationLink
     */
    setActiveLink(navigationLink: FUINavigationLink): void {
        this._activeLink.next(navigationLink);
    }

    /**
     * Toggle the modal window state
     */
    toggleModal(currentState): void {
        this._activeModal.next(currentState);
    }

    private initialiseNavigationLinksArray(initialise: (link: FUINavigationLink) => void, links: FUINavigationLink[]) {
        links.forEach(link => {
            if (link.links) {
                this.initialiseNavigationLinksArray(initialise, link.links);
            } else {
                initialise(link);
            }
        });
    }
}
